import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import {
    FileDown, FileSpreadsheet, Loader2, FileUp,
    Eye, Edit, AlertCircle, PlusCircle, Search, Trash2
} from 'lucide-react';
import {
    flexRender,
    getCoreRowModel,
    useReactTable,
    getPaginationRowModel,
    getSortedRowModel,
    getFilteredRowModel,
} from '@tanstack/react-table';
import Papa from 'papaparse';
import api from '../components/api';
import { Link } from 'react-router-dom';

const DepotsTable = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [backupModalOpen, setBackupModalOpen] = useState(false);
    const [exportingCSV, setExportingCSV] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const datePart = dateString.split(' ')[0];
            const [day,month,year] = datePart.split('-');
            return `${day}/${month}/${year}`;
        } catch (error) {
            console.error('Erreur de formatage de date:', error);
            return '-';
        }
    };

    const normalizeAuthorizationNumber = (number) => {
        if (!number || number.trim() === '') return 'Sans numéro';
        
        // Convertir en majuscules et supprimer les espaces multiples
        number = number.toUpperCase().replace(/\s+/g, ' ');
        
        // Cas à considérer comme "Sans numéro"
        const nonNumberCases = [
            'AUTORISATION',
            'LIVRE',
            'PAPIER',
            'PROPRIETAIRE',
            'ABSENT',
            'PAS',
            'RECIT',
            'COMMERCE',
            'RAS',
            'SANS',
            '1'
        ];

        // Vérifier si le texte contient des mots indiquant qu'il n'y a pas de numéro
        for (const word of nonNumberCases) {
            if (number.includes(word)) {
                return 'Sans numéro';
            }
        }

        // Si c'est un RCCM
        if (number.includes('RCCM')) {
            return number.replace(/RCCM[,.]?/g, 'RCCM ').trim();
        }

        // Si c'est un numéro simple (mais pas juste "1")
        if (/^\d+$/.test(number.replace(/[^0-9]/g, '')) && number.replace(/[^0-9]/g, '') !== '1') {
            return number.replace(/[^0-9]/g, '');
        }

        // Si c'est un numéro avec préfixe (ex: 002/GZ)
        if (/^\d+\/[A-Z]+$/.test(number.replace(/[^A-Z0-9/]/g, ''))) {
            return number.replace(/[^A-Z0-9/]/g, '');
        }

        // Si c'est un numéro avec préfixe et suffixe
        if (/^\d+\/[A-Z]+(\/[A-Z]+)*$/.test(number.replace(/[^A-Z0-9/]/g, ''))) {
            return number.replace(/[^A-Z0-9/]/g, '');
        }

        // Si c'est un numéro avec date
        if (number.includes('DU') || number.includes('DE')) {
            const parts = number.split(/DU|DE/);
            const numPart = parts[0].trim();
            // Vérifier si la partie numéro est bien un numéro (et pas juste "1")
            if (/^\d+$/.test(numPart.replace(/[^0-9]/g, '')) && numPart.replace(/[^0-9]/g, '') !== '1') {
                const datePart = parts[1] ? parts[1].trim() : '';
                return `${numPart}${datePart ? ` du ${datePart}` : ''}`;
            }
        }

        // Si c'est un numéro avec N° ou NO
        if (number.includes('N°') || number.includes('NO')) {
            const num = number.replace(/N[°O]\s*/g, '').trim();
            if (/^\d+$/.test(num.replace(/[^0-9]/g, '')) && num.replace(/[^0-9]/g, '') !== '1') {
                return num;
            }
        }

        // Si c'est un numéro avec tiret ou underscore
        if (number.includes('-') || number.includes('_')) {
            const num = number.replace(/[-_]/g, '');
            if (/^\d+$/.test(num.replace(/[^0-9]/g, '')) && num.replace(/[^0-9]/g, '') !== '1') {
                return num;
            }
        }

        // Si aucune des conditions ci-dessus n'est remplie, considérer comme "Sans numéro"
        return 'Sans numéro';
    };

    const getDepotStatus = (depot) => {
        const authNumber = normalizeAuthorizationNumber(depot.depotinfo?.numero_autorisation || '');
        return authNumber !== 'Sans numéro'
            ? { text: 'Avec numéro d\'autorisation', className: 'bg-green-100 text-green-800' }
            : { text: 'Sans numéro d\'autorisation', className: 'bg-orange-100 text-orange-800' };
    };

    const handleView = (depot) => {
        console.log('View depot:', depot);
        // Implémentez la logique de visualisation
    };

    const handleEdit = (depot) => {
        console.log('Edit depot:', depot);
        // Implémentez la logique d'édition
    };

    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce dépôt ?')) {
            try {
                await api.delete(`/collect/depot/${id}/`);
                fetchDepots(); // Rafraîchir la liste
            } catch (error) {
                alert('Erreur lors de la suppression du dépôt');
            }
        }
    };

    const columns = [
        {
            accessorKey: 'id',
            header: 'ID',
            cell: info => info.getValue(),
        },
        {
            accessorKey: 'date_collecte',
            header: 'Date de collecte',
            cell: info => formatDate(info.getValue()),
        },
        {
            accessorKey: 'region',
            header: 'Région',
            cell: info => info.getValue() || '-',
        },
        {
            accessorKey: 'departement',
            header: 'Département',
            cell: info => info.getValue() || '-',
        },
        {
            accessorKey: 'depotinfo.nom_depot',
            header: 'Nom du dépôt',
            cell: info => info.getValue() || '-',
        },
        {
            accessorKey: 'depotinfo.numero_autorisation',
            header: 'Numéro autorisation',
            cell: info => {
                const value = info.getValue();
                return normalizeAuthorizationNumber(value || '');
            },
        },
        {
            id: 'actions',
            header: 'Actions',
            cell: ({ row }) => {
                const depot = row.original;
                return (
                    <div className="flex items-center gap-2">
                        <Link
                            to={`/depots/${depot.id}`}
                            className="p-2 text-blue-600 hover:text-blue-800 rounded-full hover:bg-blue-50"
                            title="Voir les détails"
                        >
                            <Eye className="h-4 w-4" />
                        </Link>
                        <Link
                            to={`/depots/${depot.id}/edit`}
                            className="p-2 text-emerald-600 hover:text-emerald-800 rounded-full hover:bg-emerald-50"
                            title="Modifier"
                        >
                            <Edit className="h-4 w-4" />
                        </Link>
                        <button
                            onClick={() => handleDelete(depot.id)}
                            className="p-2 text-red-600 hover:text-red-800 rounded-full hover:bg-red-50"
                            title="Supprimer"
                        >
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                );
            },
        },
    ];

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        state: {
            globalFilter,
        },
        onGlobalFilterChange: setGlobalFilter,
    });

    useEffect(() => {
        fetchDepots();
    }, []);

    const fetchDepots = async () => {
        try {
            setLoading(true);
            const response = await api.get('/collect/liste-depot/');
            setData(response.data);
        } catch (error) {
            setError(error.response?.data?.message || 'Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const handleExportCSV = async () => {
        try {
            setExportingCSV(true);
            const response = await api.get('/collect/export-depots/');
            const exportData = response.data;
            const csv = Papa.unparse(exportData);
            const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.setAttribute('download', `depots_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Erreur export CSV:', error);
            alert('Erreur lors de l\'export CSV');
        } finally {
            setExportingCSV(false);
        }
    };

    const handleExportExcel = async () => {
        try {
            setExportingExcel(true);
            const response = await api.get('/collect/export-depots/');
            const exportData = response.data;
            
            const XLSX = await import('xlsx');
            const ws = XLSX.utils.json_to_sheet(exportData);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Dépôts');
            XLSX.writeFile(wb, `depots_${new Date().toISOString().split('T')[0]}.xlsx`);
        } catch (error) {
            console.error('Erreur export Excel:', error);
            alert('Erreur lors de l\'export Excel');
        } finally {
            setExportingExcel(false);
        }
    };

    const BackupModal = ({ isVisible, onClose }) => {
        const [uploadLoading, setUploadLoading] = useState(false);
        const [uploadError, setUploadError] = useState(null);

        const handleUpload = async () => {
            try {
                setUploadLoading(true);
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = '.json';

                input.onchange = async (e) => {
                    const file = e.target.files[0];
                    if (!file) return;

                    const backup = new FormData();
                    backup.append('backup', file);

                    try {
                        const response = await api.post('collect/restaurer/', backup, {
                            headers: {
                                'Accept': 'application/json',
                                'Content-Type': 'multipart/form-data'
                            }
                        });

                        if (response.status === 201) {
                            alert('Données restaurées avec succès');
                            onClose();
                            fetchDepots();
                        }
                    } catch (err) {
                        setUploadError(err.response?.data?.message || 'Erreur lors de la restauration');
                    }
                };

                input.click();
            } catch (error) {
                setUploadError('Erreur lors de la sélection du fichier');
            } finally {
                setUploadLoading(false);
            }
        };

        if (!isVisible) return null;

        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg max-w-sm w-full p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                        Restaurer une sauvegarde
                    </h3>

                    {uploadError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                            <p className="text-sm text-red-600">{uploadError}</p>
                        </div>
                    )}

                    <button
                        onClick={handleUpload}
                        disabled={uploadLoading}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
                    >
                        {uploadLoading ? (
                            <>
                                <Loader2 className="h-5 w-5 animate-spin" />
                                <span>Restauration en cours...</span>
                            </>
                        ) : (
                            <>
                                <FileUp className="h-5 w-5" />
                                <span>Sélectionner un fichier</span>
                            </>
                        )}
                    </button>

                    <button
                        onClick={onClose}
                        className="w-full mt-2 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                        Annuler
                    </button>
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                </div>
            </Layout>
        );
    }

    if (error) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
                    <div className="text-center text-red-600">
                        <p className="mt-2">{error}</p>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <BackupModal
                    isVisible={backupModalOpen}
                    onClose={() => setBackupModalOpen(false)}
                />

                <div className="mb-6 space-y-4">
                    <div className="flex justify-between items-center flex-wrap gap-4">
                        <h1 className="text-2xl font-bold text-gray-900">Liste des Dépôts</h1>
                        <div className="flex flex-wrap gap-2">
                            <Link to="/depots/create" className="btn-primary">
                                <PlusCircle className="h-4 w-4" />
                                <span className="hidden sm:inline">Nouveau Dépôt</span>
                            </Link>
                            <button 
                                className="btn-secondary" 
                                onClick={handleExportCSV}
                                disabled={exportingCSV}
                            >
                                {exportingCSV ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <FileDown className="h-4 w-4" />
                                )}
                                <span className="hidden sm:inline">
                                    {exportingCSV ? 'Export CSV...' : 'CSV'}
                                </span>
                            </button>
                            <button 
                                className="btn-secondary" 
                                onClick={handleExportExcel}
                                disabled={exportingExcel}
                            >
                                {exportingExcel ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <FileSpreadsheet className="h-4 w-4" />
                                )}
                                <span className="hidden sm:inline">
                                    {exportingExcel ? 'Export Excel...' : 'Excel'}
                                </span>
                            </button>
                        </div>
                    </div>
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                        <input
                            value={globalFilter ?? ''}
                            onChange={e => setGlobalFilter(e.target.value)}
                            className="w-full md:max-w-xs pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            placeholder="Rechercher..."
                        />
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                {table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id}>
                                        {headerGroup.headers.map(header => (
                                            <th
                                                key={header.id}
                                                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                                                onClick={header.column.getToggleSortingHandler()}
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody className="divide-y divide-gray-200 bg-white">
                                {table.getRowModel().rows.map(row => (
                                    <tr key={row.id} className="hover:bg-gray-50">
                                        {row.getVisibleCells().map(cell => (
                                            <td key={cell.id} className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200 bg-gray-50">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => table.previousPage()}
                                disabled={!table.getCanPreviousPage()}
                                className="btn-secondary"
                            >
                                Précédent
                            </button>
                            <button
                                onClick={() => table.nextPage()}
                                disabled={!table.getCanNextPage()}
                                className="btn-secondary"
                            >
                                Suivant
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Affichage de{' '}
                                    <span className="font-medium">{table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}</span>
                                    {' '}à{' '}
                                    <span className="font-medium">
                                        {Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, table.getFilteredRowModel().rows.length)}
                                    </span>
                                    {' '}sur{' '}
                                    <span className="font-medium">{table.getFilteredRowModel().rows.length}</span>
                                    {' '}résultats
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => table.previousPage()}
                                    disabled={!table.getCanPreviousPage()}
                                    className="btn-secondary"
                                >
                                    Précédent
                                </button>
                                <button
                                    onClick={() => table.nextPage()}
                                    disabled={!table.getCanNextPage()}
                                    className="btn-secondary"
                                >
                                    Suivant
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default DepotsTable;